import { useEffect, useRef, useState } from 'react'
import styles from '../DavidPifView.module.css'
import classNames from 'classnames'
import { Text } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { DavidButton } from './DavidButton'

export function StoriesSongsSection() {
  const { t } = useTranslate('pif')
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [playerReady, setPlayerReady] = useState(false)
  const baseVideoUrl =
    'https://www.youtube-nocookie.com/embed/O_HYdZ-Jq3Q?enablejsapi=1&playsinline=1&autoplay=0&mute=1&rel=0&modestbranding=1&controls=1'
  // Initialize with a server-safe value to avoid SSR hydration diffs
  const [videoSrc, setVideoSrc] = useState<string>(baseVideoUrl)

  useEffect(() => {
    try {
      const origin = window.location.origin
      setVideoSrc(`${baseVideoUrl}&origin=${encodeURIComponent(origin)}`)
    } catch {
      setVideoSrc(baseVideoUrl)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
    const iframe = iframeRef.current
    const container = containerRef.current
    if (!iframe || !container) return

    const send = (func: string, args: unknown[] = []) => {
      iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), '*')
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!playerReady) return
        if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
          send('playVideo')
        } else {
          send('pauseVideo')
        }
      },
      { threshold: [0, 0.5] },
    )

    io.observe(container)

    const onVis = () => {
      if (document.hidden) send('pauseVideo')
    }
    document.addEventListener('visibilitychange', onVis)

    return () => {
      io.disconnect()
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [playerReady])

  return (
    <section className={classNames('relative w-full bg-[#48c7d8]', styles['section-lg'])}>
      <div className="relative mx-auto flex w-full max-w-[1440px] shrink-0 flex-col-reverse content-stretch items-start gap-10 lg:gap-[96px]">
        {/* Content */}
        <div className="relative flex w-full max-w-[680px] shrink-0 flex-col content-stretch items-start gap-8 lg:gap-10">
          {/* Copy */}
          <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[12px] not-italic text-[#141447]">
            <p className="font-angel-royal relative w-full text-5xl lowercase not-italic leading-[2.5rem] text-[#141447] md:text-[3.5rem] md:leading-[3rem]">
              {t('davidStoriesSongsTitle', 'Stories and Songs That Stay for a Lifetime')}
            </p>
            <Text weight="medium" className="photon-paragraph-md md:photon-paragraph-lg">
              {t(
                'davidStoriesSongsDescription',
                "Animated musicals shape generations. The songs and heroes kids grow up with help define who they become. When they see David's faith, courage, and heart — they'll carry those lessons for life.",
              )}
            </Text>
          </div>

          <DavidButton variant="dark" openPifOverlay className="w-full md:w-auto">
            {t('payItForward', 'Pay it Forward')}
          </DavidButton>
        </div>

        {/* Video */}
        <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-[1rem] md:items-end md:gap-[1.5rem]">
          <p className="font-whitney-medium relative hidden shrink-0 whitespace-pre text-nowrap text-right text-[12px] not-italic leading-[16px] text-[#141447] lg:block">
            {t('davidFollowTheLightCaption', 'Watch the teaser trailer for the song "Follow the Light"')}
          </p>
          <div
            ref={containerRef}
            className="aspect-h-9 aspect-w-16 relative w-full overflow-hidden rounded-2xl lg:rounded-3xl"
          >
            <iframe
              ref={iframeRef}
              src={videoSrc}
              title="Follow the Light - DAVID Teaser Trailer"
              className="h-full w-full"
              allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              onLoad={() => setPlayerReady(true)}
            />
          </div>
          <p className="font-whitney-medium relative block shrink-0 whitespace-pre text-nowrap text-right text-[12px] not-italic leading-[16px] text-[#141447] lg:hidden">
            {t('davidFollowTheLightCaption', 'Watch the teaser trailer for the song "Follow the Light"')}
          </p>
        </div>
      </div>
    </section>
  )
}
